import { useRef, useEffect, useState } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useBlockSlot,
  useContentToken,
  useSlotGroup,
  SectionContextProvider,
  EditableToken,
  EditableSlotGroup,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';

// ============================================================================
// Guided-tour detection
// ----------------------------------------------------------------------------
// The multihub-tour-scheduler block resolves available tour types async from
// PMS APIs and does not expose that list to the host. Detect guided tours from
// the rendered wizard copy instead:
//   - Tour-type card title is exactly "Guided Tour" (not "Self-Guided Tour")
//   - Solo guided option skips the type step → header "Schedule Your Guided Tour"
// Once seen, stay true for the rest of the wizard (type cards unmount on later
// steps). Sticky so selecting Live Video mid-flow does not hide the disclaimer.
// ============================================================================

function formOffersGuidedTour(root: HTMLElement): boolean {
  for (const heading of root.querySelectorAll('h4')) {
    if (heading.textContent?.trim() === 'Guided Tour') return true;
  }
  for (const heading of root.querySelectorAll('h3')) {
    if (heading.textContent?.trim() === 'Schedule Your Guided Tour') return true;
  }
  return false;
}

// ============================================================================
// Schema definitions (module-level literals — extractor follows one level)
// ============================================================================

const bgImageDef: SlotGroupDefinition = {
  label: 'Parallax Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'Schedule a tour background',
    },
  },
  sourceBinding: {
    collection: 'photo_gallery',
    defaultIndex: 0,
    fieldMappings: { image: 'image', alt: 'alt_text' },
  },
};

// ============================================================================
// Component
// ============================================================================

export function ScheduleTourForm() {
  const prefix = 'scheduletourform';
  const formBlockPath = `${prefix}.form_block`;

  const formSlot = useBlockSlot(formBlockPath, {
    defaultValue: 'multihub-tour-scheduler',
  });
  const disclaimer = useContentToken('scheduletourform.disclaimer');

  const bgImage = useSlotGroup('scheduletourform.bg_image', bgImageDef);

  // Parallax refs
  const parallaxSectionRef = useRef<HTMLDivElement>(null);
  const imgWrapRef = useRef<HTMLDivElement>(null);
  const formWrapRef = useRef<HTMLDivElement>(null);
  const [guidedToursAvailable, setGuidedToursAvailable] = useState(false);

  useEffect(() => {
    const section = parallaxSectionRef.current;
    const imgWrap = imgWrapRef.current;
    if (!section || !imgWrap) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const onScroll = () => {
      const rect = section.getBoundingClientRect();
      const scrolled = -rect.top;
      if (scrolled < -window.innerHeight || scrolled > section.offsetHeight) return;
      imgWrap.style.transform = `translateY(${scrolled * 0.3}px)`;
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Watch the scheduler block for guided-tour options (see formOffersGuidedTour).
  useEffect(() => {
    const root = formWrapRef.current;
    if (!root) return;

    const check = () => {
      if (formOffersGuidedTour(root)) setGuidedToursAvailable(true);
    };

    check();
    const observer = new MutationObserver(check);
    observer.observe(root, { childList: true, subtree: true, characterData: true });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Pinned parallax background + centered scheduler */}
      <div ref={parallaxSectionRef} className="stf-parallax-section">
        {/* Parallax background image */}
        <EditableSlotGroup groupPath="scheduletourform.bg_image" definition={bgImageDef}>
          <div ref={imgWrapRef} className="stf-parallax-img-wrap">
            {bgImage.image ? (
              <VibesitesImage
                url={bgImage.image}
                alt={bgImage.alt || ''}
                priority
                className="stf-parallax-img"
              />
            ) : (
              <div className="stf-parallax-img-fallback" />
            )}
          </div>
        </EditableSlotGroup>

        {/* Dark overlay for legibility */}
        <div className="stf-parallax-overlay" />

        {/* Centered form + disclaimer */}
        <div className="stf-parallax-content">
          <div className="stf-form-column">
            <div ref={formWrapRef} className="stf-form-wrap">
              <EditableToken path={formBlockPath} as="div">
                {formSlot ? (
                  <SectionContextProvider sectionId={formSlot.sectionId}>
                    <formSlot.BlockComponent sectionId={formSlot.sectionId} />
                  </SectionContextProvider>
                ) : (
                  <div className="stf-form-empty-card">
                    <p className="stf-form-empty">No form block selected</p>
                  </div>
                )}
              </EditableToken>
            </div>
            {guidedToursAvailable ? (
              <EditableToken path="scheduletourform.disclaimer" as="p" className="stf-disclaimer">
                {disclaimer}
              </EditableToken>
            ) : (
              <EditableToken
                path="scheduletourform.disclaimer"
                as="p"
                className="hidden"
                style={{ display: 'none' }}
              >
                {''}
              </EditableToken>
            )}
          </div>
        </div>
      </div>

      {/* Schema-lint anchors — static paths so the linter can match each schema key. */}
      <EditableToken path="scheduletourform.form_block" as="div" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>

      <style>{`
        /* ── Parallax section ── */
        .stf-parallax-section {
          position: relative;
          overflow: hidden;
          min-height: 0;
          padding: 1.25rem 1.5rem;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        @media (min-width: 768px) {
          .stf-parallax-section {
            min-height: 520px;
            padding: 6rem 2rem;
          }
        }

        /* Oversized image wrapper gives the parallax translate room to move */
        .stf-parallax-img-wrap {
          position: absolute;
          inset-inline: 0;
          top: -20%;
          height: 140%;
          will-change: transform;
        }
        .stf-parallax-img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .stf-parallax-img-fallback {
          width: 100%;
          height: 100%;
          background: var(--vibe-color-text, #1f2937);
        }

        .stf-parallax-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(to bottom, rgba(0,0,0,0.58), rgba(0,0,0,0.48));
          pointer-events: none;
        }

        .stf-parallax-content {
          position: relative;
          z-index: 10;
          width: 100%;
          display: flex;
          justify-content: center;
        }

        .stf-form-column {
          width: 100%;
          max-width: 640px;
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .stf-form-wrap {
          width: 100%;
          border-radius: 0;
          overflow: hidden;
          box-shadow: 0 8px 40px rgba(0, 0, 0, 0.25);
        }

        .stf-disclaimer {
          margin: 0;
          padding: 0 0.25rem;
          font-family: var(--vibe-font-body);
          font-size: 0.875rem;
          line-height: 1.45;
          text-align: center;
          color: rgba(255, 255, 255, 0.88);
        }
        /* Wizard heading — hard-coded Poppins semibold (not theme font tokens). */
        .stf-form-wrap h3 {
          font-family: "Poppins", sans-serif !important;
          font-weight: 600 !important;
          text-transform: uppercase;
        }

        /* ── Tour-scheduler block card exterior — square corners ──
           The multihub-tour-scheduler block renders its outer card with a
           Tailwind rounded-lg radius (the only rounded-lg + shadow-xl element
           in the block). Square it so the form's exterior matches the sharp
           cards used elsewhere on the site. Tailwind utilities are not
           !important, so this stylesheet !important wins. */
        .stf-form-wrap .rounded-lg.shadow-xl {
          border-radius: 0 !important;
        }

        /* ── Primary CTA — match the site's filled pill ──
           The tour-scheduler block renders its Continue / Schedule Tour button
           with an md radius from Tailwind utilities. Round it to a fully-rounded
           pill with the same hover lift as the header's .nav-cta-btn (mirrors
           the sibling ContactForm override) so it reads as a site CTA. Force
           heading font so it matches Header › contact_link (the block defaults
           to font-body). The .bg-primary.ml-auto selector is unique to the nav
           primary button, so calendar day and time-slot buttons keep their
           own shapes. */
        .stf-form-wrap button.bg-primary.ml-auto {
          border-radius: 9999px !important;
          font-family: var(--vibe-font-heading) !important;
          transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s !important;
        }
        .stf-form-wrap button.bg-primary.ml-auto:hover:not(:disabled) {
          background-color: color-mix(in srgb, var(--vibe-color-primary), black 12%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .stf-form-wrap button.bg-primary.ml-auto:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: none;
        }

        /* ── Inline "Contact us" text link (tour-type footer) ──
           The scheduler renders "Not seeing the option… Contact us" as a plain
           <a> with no underline. Underline it so it reads as clickable. Exclude
           a.bg-primary — that's the filled empty-state CTA below. */
        .stf-form-wrap a:not(.bg-primary) {
          text-decoration: underline;
          text-underline-offset: 0.15em;
        }

        /* ── "Contact Us" empty-state CTA — match the header's .nav-cta-btn ──
           When no tour options are available the block renders a fallback
           "Contact Us" link as an md-radius filled anchor. Give it the header
           CTA's fully-rounded pill, uppercase label, letter-spacing, and hover
           lift so the two read identically. a.bg-primary is unique to this
           anchor (the tour-type footer "Contact us" link has no fill). */
        .stf-form-wrap a.bg-primary {
          border-radius: 9999px !important;
          font-family: var(--vibe-font-heading) !important;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          font-weight: 400;
          transition: background-color 0.2s, transform 0.1s, box-shadow 0.2s !important;
        }
        .stf-form-wrap a.bg-primary:hover {
          background-color: color-mix(in srgb, var(--vibe-color-primary), black 12%) !important;
          transform: translateY(-1px);
          box-shadow:
            0 2px 6px rgba(0, 0, 0, 0.1),
            0 4px 16px color-mix(in srgb, var(--vibe-color-primary) 25%, transparent);
        }
        .stf-form-wrap a.bg-primary:active {
          transform: translateY(0);
          box-shadow: none;
        }

        .stf-form-empty-card {
          background: white;
          border-radius: 0;
          padding: 2rem;
        }
        .stf-form-empty {
          font-family: var(--vibe-font-body);
          font-size: 0.95rem;
          color: var(--vibe-color-text-muted);
          text-align: center;
          margin: 0;
          padding: 2rem 0;
        }

        @media (prefers-reduced-motion: reduce) {
          .stf-parallax-img-wrap { transform: none !important; }
        }
      `}</style>
    </>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'ScheduleTourForm',
  displayName: 'Schedule A Tour Form',
  tokens: {
    'scheduletourform.form_block': {
      label: 'Form Block',
      type: 'block',
      defaultValue: 'multihub-tour-scheduler',
      acceptedBlockCategories: ['Forms'],
    },
    'scheduletourform.disclaimer': {
      label: 'Disclaimer',
      type: 'text',
      defaultValue: 'Guided tours are by appointment only.',
    },
  },
  slotGroups: {
    'scheduletourform.bg_image': bgImageDef,
  },
};

(ScheduleTourForm as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default ScheduleTourForm;
